/**
 * Open a file "the preferred way" from Browse or Search — the same decision
 * the Desktop makes (lib/file-openers.ts): straight into an arche when that is
 * where the kind belongs, else the full-screen viewer (which offers the
 * arches that can open it), else a download.
 */
import { useCallback, useState } from 'react';
import type { VaultDocumentDto } from '@/lib/api';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import { FileViewer } from '../components/shared/FileViewer';
import { downloadVaultDocument } from '../lib/download';
import { doubleClickAction, type FileApp } from '../lib/file-openers';

export function useOpenFile() {
  const ai = useGlobalFiasAIOptional();
  const [viewerDoc, setViewerDoc] = useState<VaultDocumentDto | null>(null);

  /** Hand the file to an arche (the platform's open-document handoff). */
  const openInApp = useCallback(
    (doc: VaultDocumentDto, app: FileApp) => {
      const routed = ai?.openHandoff(
        { kind: 'open_document', documentId: doc.documentId },
        app.archeId,
      );
      // Nowhere to hand it (no assistant host, or the arche refused): show it.
      if (!routed) setViewerDoc(doc);
    },
    [ai],
  );

  const open = useCallback(
    (doc: VaultDocumentDto) => {
      const action = doubleClickAction(doc);
      if (action.kind === 'app') openInApp(doc, action.app);
      else if (action.kind === 'preview') setViewerDoc(doc);
      else void downloadVaultDocument(doc);
    },
    [openInApp],
  );

  const ui = (
    <FileViewer
      document={viewerDoc}
      onClose={() => setViewerDoc(null)}
      onDownload={(doc) => void downloadVaultDocument(doc)}
      onOpenInApp={ai ? openInApp : undefined}
    />
  );

  return { open, preview: setViewerDoc, openInApp, viewerDoc, ui };
}
